import "react-toastify/dist/ReactToastify.css";

import { AnimatePresence } from "framer-motion";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import AllUsers from "./pages/AdminPages/AllUsers";
import Auth from "./pages/Auth";
import AuthContext from "./contexts/AuthCtx";
import CGU from "./pages/CGU";
import CGV from "./pages/CGV";
import CertificationRedirect from "./pages/Certification/CertificationRedirect";
import Dashboard from "./pages/Dashboard";
import Expedition from "./pages/AdminPages/Expedition";
import LifetimeExpedition from "./pages/AdminPages/LifetimeExpedition";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import GoogleCallback from "./pages/Auth/GoogleCallback";
import Home from "./pages/Home";
import Letter from "./pages/Letter/Letter";
import Login from "./pages/Auth/Login";
import MentionsLegales from "./pages/mentions-legales";
import Onboarding from "./pages/Onboarding/Onboarding";
import PC from "./pages/PC";
import PaymentError from "./pages/PaymentError/PaymentError";
import PaymentSuccess from "./pages/PaymentSuccess/PaymentSuccess";
import Register from "./pages/Auth/Register";
import ResetPassword from "./pages/Auth/ResetPassword";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import Settings from "./pages/Settings";
import { ToastContainer } from "react-toastify";
import { useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { pageMotionProps } from "./motion/transitions";
import { useRouteVariant } from "./motion/useRouteVariant";
import { getPostAuthPath } from "./utils/authRedirect";

const PUBLIC_PATHS = [
  "/",
  "/home",
  "/pc",
  "/ml",
  "/cgu",
  "/cgv",
  "/signup",
  "/login",
  "/forgot-password",
];

const isPublicPath = (pathname) =>
  PUBLIC_PATHS.includes(pathname) ||
  pathname.startsWith("/auth/") ||
  pathname.startsWith("/reset-password/") ||
  pathname.startsWith("/verify-email/");

function AppFallback() {
  const { isLoggedIn, userStatus } = useContext(AuthContext);

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (userStatus === "certified") {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/onboarding" replace />;
}

function App() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;
  const { isLoggedIn, userStatus, isAdmin, isAuthReady } = AuthCtx;

  useEffect(() => {
    if (!AuthCtx.userToken && !isLoggedIn && !isPublicPath(pathname)) {
      navigate("/login", { replace: true });
    }
  }, [AuthCtx.userToken, isLoggedIn, navigate, pathname]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (pathname === "/signup" || pathname === "/login") {
      navigate(getPostAuthPath(userStatus), { replace: true });
    }
  }, [isLoggedIn, pathname, userStatus, navigate]);

  const { variant, transitionKey } = useRouteVariant();

  if (!isAuthReady) {
    return null;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar
        closeButton={false}
        newestOnTop
        icon={false}
        toastClassName="app-toast"
        bodyClassName="app-toast-body"
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={transitionKey}
          className="min-h-screen w-full"
          {...pageMotionProps(variant)}
        >
          <Routes location={location}>
            <Route path="/pc" element={<PC />} />
            <Route path="/ml" element={<MentionsLegales />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/home" element={<Home />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route
              path="/signup"
              element={
                isLoggedIn ? (
                  <Navigate to={getPostAuthPath(userStatus)} replace />
                ) : (
                  <Register />
                )
              }
            />
            <Route
              path="/login"
              element={
                isLoggedIn ? (
                  <Navigate to={getPostAuthPath(userStatus)} replace />
                ) : (
                  <Login />
                )
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/auth/:token" element={<Auth />} />
            <Route path="/certification/legal" element={<CertificationRedirect />} />
            <Route path="/certification" element={<CertificationRedirect />} />

            {isLoggedIn && userStatus !== "certified" && (
              <>
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/dashboard" element={<Navigate to="/onboarding" replace />} />
                <Route path="/settings" element={<Navigate to="/onboarding" replace />} />
                <Route path="/letter/:selectedLetter" element={<Navigate to="/onboarding" replace />} />
              </>
            )}

            {isLoggedIn && userStatus === "certified" && (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/letter/:selectedLetter" element={<Letter />} />
                <Route path="/success" element={<PaymentSuccess />} />
                <Route path="/error" element={<PaymentError />} />
                <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
              </>
            )}

            {isLoggedIn && isAdmin && (
              <>
                <Route path="/admin/all-users" element={<AllUsers />} />
                <Route path="/admin" element={<AllUsers />} />
                <Route path="/admin/expedition" element={<Expedition />} />
                <Route path="/admin/lifetime-sends" element={<LifetimeExpedition />} />
                <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
              </>
            )}

            <Route path="*" element={<AppFallback />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
}

export default App;
